import { ChevronRightIcon, TagIcon, XIcon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardFooter } from '@/components/card/card'
import { Combobox } from '@/components/combobox/combobox'
import { Divider } from '@/components/divider/divider'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { MultiSelect } from '@/components/multi-select/multi-select'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/select/select'

import { cn } from '@/lib/utils'

import { DisclosureSettingSwitch } from '../../../dialog-settings/disclosure-setting-switch'
import { VISA_TYPE_SELECT_ITEMS } from './constants'
import { SkipSwitch } from './skip-switch/skip-switch'
import { useRegisterNvcCase } from './use-register-nvc-case'

export const RegisterNvcCase = () => {
  const {
    form,
    formRef,
    formErrors,
    applicantId,
    categoryId,
    visaType,
    locationList,
    defaultCategories,
    dataListCategory,
    organizationMembers,
    userId,
    isAdmin,
    isCasePublic,
    isSkipActivated,
    isVisaTypeSelected,
    isNonimmigrantVisa,
    isDisabledCategoryNameSaveButton,
    isDisabledSubmitButton,
    handleCategoryNameSave,
    handleSubmit,
  } = useRegisterNvcCase()

  return (
    <Card>
      <Form {...form}>
        <form ref={formRef}>
          <CardContent className="p-6">
            <div className="mb-2 text-sm text-muted-foreground">Please enter the case information.</div>
            <div className="space-y-4">
              <>
                {categoryId !== 'add-category' && (
                  <FormField
                    control={form.control}
                    rules={{ required: true }}
                    name="categoryId"
                    render={({ field }) => (
                      <FormItem autoFocus>
                        <FormLabel>Category*</FormLabel>
                        <Select onValueChange={field.onChange} value={categoryId} disabled={!applicantId}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={<span className="text-faint-foreground">Select</span>} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="flex-1">
                            <SelectGroup>
                              {dataListCategory?.map((category, i) => (
                                <SelectItem key={i} value={String(category.id)}>
                                  {category.path_name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                            <SelectSeparator />
                            <SelectGroup>
                              {defaultCategories
                                .filter((x) => {
                                  const isDuplicate = dataListCategory?.some((y) => y.path_name === x.label)
                                  return !isDuplicate
                                })
                                .map((category, i) => (
                                  <SelectItem key={i} value={String(category.id)}>
                                    {category.label}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                            <SelectSeparator />
                            <SelectItem value="add-category">
                              Add a category
                              <div className="absolute bottom-0 right-3 top-0 flex items-center justify-center">
                                <ChevronRightIcon className="text-muted-foreground" size={16} />
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>Categorize cases for efficient management.</FormDescription>
                      </FormItem>
                    )}
                  />
                )}
                {categoryId === 'add-category' && (
                  <div className="space-y-2">
                    <FormField
                      control={form.control}
                      name="categoryName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category*</FormLabel>
                          <FormControl>
                            <Card className="shadow">
                              <CardContent className="p-0">
                                <div className="flex items-center">
                                  <TagIcon className="ml-4 text-muted-foreground" size={16} />
                                  <Input
                                    rootClassName={cn('flex-1')}
                                    className="border-0 placeholder:text-faint-foreground"
                                    placeholder="Enter a category name"
                                    {...field}
                                  />
                                  <div
                                    className="cursor-pointer p-2"
                                    onClick={() => {
                                      form.resetField('categoryId')
                                    }}
                                  >
                                    <XIcon size={16} />
                                  </div>
                                </div>
                                <div className="h-[1px] w-full bg-border" />
                                <div className="p-5">
                                  <Button
                                    disabled={isDisabledCategoryNameSaveButton}
                                    className="w-full"
                                    type="button"
                                    onClick={handleCategoryNameSave}
                                  >
                                    Save
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </>

              <FormField
                control={form.control}
                rules={{ required: true }}
                name="visaType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visa Type*</FormLabel>
                    <Select onValueChange={field.onChange} value={visaType}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={<span className="text-faint-foreground">Select</span>} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="flex-1">
                        <SelectGroup>
                          {VISA_TYPE_SELECT_ITEMS.map(({ label, value }, i) => (
                            <SelectItem key={i} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              {isVisaTypeSelected && (
                <>
                  {/* Location */}
                  <FormField
                    control={form.control}
                    rules={{ required: true }}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location*</FormLabel>
                        <Combobox
                          modal
                          items={
                            locationList?.map((location) => ({
                              label: location,
                              value: location,
                            })) ?? []
                          }
                          placeholder="Select location"
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  {/* Application ID or Case Number */}
                  <FormField
                    control={form.control}
                    rules={{ required: true }}
                    name="caseNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Application ID or Case Number*</FormLabel>
                        <FormControl>
                          <Input
                            className="uppercase placeholder:text-faint-foreground"
                            placeholder="MTL1999626025"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Tags */}
                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <FormControl>
                          <Input
                            className="placeholder:text-faint-foreground"
                            placeholder="e.g. H1B, Green Card, etc."
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className={cn(!!formErrors.tags?.message && 'text-red-500')}>
                          {formErrors.tags?.message ?? 'Assign one or more tags to the case, separated by commas.'}
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  {isNonimmigrantVisa && (
                    <>
                      <p className={cn('text-sm font-medium text-[#0f172a]')}>
                        If the form was completed before January 1, 2022, please activate Skip switch below.
                      </p>
                      <FormField
                        control={form.control}
                        name="isSkipActivated"
                        render={({ field: { value, onChange, ...restFields } }) => (
                          <FormItem>
                            <FormControl>
                              <SkipSwitch value={value} onChange={onChange} {...restFields} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      {!isSkipActivated && (
                        <>
                          <FormField
                            control={form.control}
                            name="passportNumber"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Passport Number</FormLabel>
                                <FormControl>
                                  <Input
                                    className="placeholder:text-faint-foreground"
                                    placeholder="Applicant Passport Number"
                                    {...field}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="surname"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>First 5 Letters of Surname</FormLabel>
                                <FormControl>
                                  <Input
                                    className="placeholder:text-faint-foreground"
                                    placeholder="Applicant Surname"
                                    maxLength={5}
                                    {...field}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </>
                      )}
                    </>
                  )}

                  <Divider className="!mb-4 !mt-6" />

                  {/* Case settings */}
                  <FormField
                    control={form.control}
                    name="isPublic"
                    render={({ field: { value, onChange, ...restFields } }) => (
                      <FormItem>
                        <FormControl>
                          <DisclosureSettingSwitch value={value} onChange={onChange} {...restFields} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {isAdmin && !isCasePublic && (
                    <FormField
                      control={form.control}
                      name="assignees"
                      render={({ field: { value, onChange } }) => (
                        <FormItem>
                          <FormLabel>Assignees</FormLabel>
                          <FormControl>
                            <MultiSelect
                              asChild
                              portalContainerRef={formRef}
                              defaultValue={userId ? [String(userId)] : []}
                              options={organizationMembers}
                              placeholder="Select assignees"
                              maxCount={5}
                              value={value}
                              onValueChange={(selectedMembers) => onChange(selectedMembers)}
                            />
                          </FormControl>
                          <FormDescription>You can add multiple assignees.</FormDescription>
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}
            </div>
          </CardContent>

          {/* SUBMIT */}
          <CardFooter>
            <Button disabled={isDisabledSubmitButton} onClick={handleSubmit}>
              Save
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
